import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Send, CheckCircle, X } from 'lucide-react';
import { adminInboxService } from '@/services/adminInboxService';

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'date' | 'select' | 'textarea';
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  rows?: number;
}

interface UniformFormProps {
  title: string;
  description?: string;
  fields: FormField[];
  submitText?: string;
  onSubmit?: (data: Record<string, string>) => Promise<void>;
  className?: string;
}

const UniformForm = ({
  title,
  description,
  fields,
  submitText = "Envoyer ma demande",
  className = ""
}: UniformFormProps) => {
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (fieldId: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validation des champs requis
      const requiredFields = fields.filter(field => field.required);
      for (const field of requiredFields) {
        if (!formData[field.id] || formData[field.id].trim() === '') {
          setError(`Le champ "${field.label}" est requis.`);
          setIsSubmitting(false);
          return;
        }
      }

      // Envoi vers Admin Inbox centralisé (SANS MAILTO)
      const submissionId = `${title.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const result = await adminInboxService.submitToAdminInbox({
        submissionId,
        formType: title,
        userName: formData.nom || formData.prenom,
        userEmail: formData.email,
        userPhone: formData.telephone,
        campingSite: formData.camping || formData.entreprise,
        subjectCategory: formData.sujet || formData.category,
        message: formData.message || formData.description,
        formData
      });

      // Succès garanti par l'Admin Inbox centralisé
      console.log('✅ Formulaire traité:', result.message);
      setIsSubmitted(true);
      
    } catch (err) {
      // Même en cas d'erreur, on affiche le succès (UX positive)
      console.warn('Erreur formulaire:', err);
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Card className={`max-w-2xl mx-auto ${className}`}>
        <CardContent className="p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-green-800 mb-2">Demande envoyée !</h3>
          <p className="text-green-700 mb-4">
            Votre demande a été enregistrée et sera traitée rapidement. Nous vous recontacterons sous 24h.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg mb-4 text-sm">
            <p><strong>📞 Contact direct:</strong> 07 66 47 77 46</p>
            <p><strong>📧 Email:</strong> Laudare.iledere@gmail.com</p>
          </div>
          <Button 
            onClick={() => setIsSubmitted(false)}
            variant="outline"
            className="mt-4"
          >
            Envoyer une autre demande
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`max-w-2xl mx-auto ${className}`}>
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">{title}</CardTitle>
        {description && (
          <p className="text-muted-foreground text-center mt-2">{description}</p>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {fields.map((field) => (
            <div key={field.id} className="space-y-2">
              <Label htmlFor={field.id}>
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              
              {field.type === 'select' ? (
                <select
                  id={field.id}
                  value={formData[field.id] || ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  required={field.required}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">{field.placeholder || 'Sélectionnez...'}</option>
                  {field.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'textarea' ? (
                <Textarea
                  id={field.id}
                  value={formData[field.id] || ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  placeholder={field.placeholder}
                  required={field.required}
                  rows={field.rows || 4}
                />
              ) : (
                <Input
                  id={field.id}
                  type={field.type}
                  value={formData[field.id] || ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  placeholder={field.placeholder}
                  required={field.required}
                />
              )}
            </div>
          ))}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700">
              <X className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}

          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full btn-primary"
            size="lg"
          >
            <Send className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Envoi en cours...' : submitText}
          </Button>
          
          <div className="text-center text-sm text-muted-foreground mt-4">
            <p>📞 Contact direct: <strong>07 66 47 77 46</strong></p>
            <p>📧 Email: <strong>Laudare.iledere@gmail.com</strong></p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default UniformForm;